import React, { useState, useEffect } from 'react';
import { Menu, X, Ticket, Handshake, HelpCircle, BookOpen, Wine, Map as MapIcon, Sparkles, ShoppingBag } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { eventInfo, images } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (isHomePage) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollTo: id } });
    }
    setIsMobileMenuOpen(false);
  };

  const openTickets = () => {
    if (eventInfo.ticketUrl) {
      window.open(eventInfo.ticketUrl, '_blank');
    } else {
      scrollToSection('tickets');
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header 
        className={`fixed top-[30px] sm:top-[34px] left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled 
            ? 'bg-black/90 backdrop-blur-md shadow-lg py-2' 
            : 'bg-transparent py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between gap-6">
          {/* Logo + primary left-side nav */}
          <div className="flex items-center gap-6 lg:gap-8 min-w-0">
            <div className="cursor-pointer shrink-0" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img 
                src={images.logo} 
                alt="Aloha Tequila & Arts Festival" 
                className={`transition-all duration-300 ${isScrolled ? 'h-12' : 'h-16'} object-contain`}
              />
            </div>

            {/* Primary nav (top-left) — Welcome / Tequila / Map */}
            <nav className="hidden md:flex items-center gap-5 lg:gap-7">
              <Link 
                to="/welcome"
                className="text-white/85 hover:text-pink-400 transition-colors font-semibold flex items-center gap-1.5 text-sm lg:text-base"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="header-welcome-link"
              >
                <Sparkles className="w-4 h-4" />
                Welcome
              </Link>
              <Link 
                to="/tequila"
                className="text-white/85 hover:text-pink-400 transition-colors font-semibold flex items-center gap-1.5 text-sm lg:text-base"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="header-tequila-link"
              >
                <Wine className="w-4 h-4" />
                Tequila
              </Link>
              <Link 
                to="/map"
                className="text-white/85 hover:text-pink-400 transition-colors font-semibold flex items-center gap-1.5 text-sm lg:text-base"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="header-map-link"
              >
                <MapIcon className="w-4 h-4" />
                Map
              </Link>
              <Link 
                to="/merch"
                className="text-white/85 hover:text-pink-400 transition-colors font-semibold flex items-center gap-1.5 text-sm lg:text-base"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="header-merch-link"
              >
                <ShoppingBag className="w-4 h-4" />
                Merch
              </Link>
            </nav>
          </div>

          {/* Desktop Navigation — secondary (right side) */}
          <nav className="hidden md:flex items-center gap-5 lg:gap-7">
            <button 
              onClick={() => scrollToSection('about')}
              className="text-white/70 hover:text-pink-400 transition-colors font-medium text-sm lg:text-base"
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('tickets')}
              className="text-white/70 hover:text-pink-400 transition-colors font-medium text-sm lg:text-base"
            >
              Tickets
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-white/70 hover:text-pink-400 transition-colors font-medium text-sm lg:text-base"
            >
              Contact
            </button>
            <Link 
              to="/vendor-sponsor"
              className="text-white/70 hover:text-pink-400 transition-colors font-medium flex items-center gap-1 text-sm lg:text-base"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Handshake className="w-4 h-4" />
              Vendors/Sponsors
            </Link>
            <Link 
              to="/faq"
              className="text-white/70 hover:text-pink-400 transition-colors font-medium flex items-center gap-1 text-sm lg:text-base"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <HelpCircle className="w-4 h-4" />
              FAQs
            </Link>
            <Link 
              to="/blog"
              className="text-white/70 hover:text-pink-400 transition-colors font-medium flex items-center gap-1 text-sm lg:text-base"
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="header-blog-link"
            >
              <BookOpen className="w-4 h-4" />
              Blog
            </Link>
            <Button 
              onClick={openTickets}
              className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold rounded-full px-6 transition-all duration-300 hover:scale-105 flex items-center gap-2"
            >
              <Ticket className="w-4 h-4" />
              Get Tickets
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div 
        className={`fixed inset-0 z-30 bg-black/95 backdrop-blur-md transition-all duration-300 md:hidden ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <nav className="flex flex-col items-center justify-center h-full gap-8">
          <img 
            src={images.logo} 
            alt="Aloha Tequila & Arts Festival" 
            className="h-32 object-contain mb-8"
          />
          <button 
            onClick={() => scrollToSection('about')}
            className="text-white text-2xl font-bold hover:text-pink-400 transition-colors"
          >
            About
          </button>
          <button 
            onClick={() => scrollToSection('tickets')}
            className="text-white text-2xl font-bold hover:text-pink-400 transition-colors"
          >
            Tickets
          </button>
          <button 
            onClick={() => scrollToSection('contact')}
            className="text-white text-2xl font-bold hover:text-pink-400 transition-colors"
          >
            Contact
          </button>
          <Link 
            to="/vendor-sponsor"
            className="text-white text-2xl font-bold hover:text-pink-400 transition-colors flex items-center gap-2"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <Handshake className="w-6 h-6" />
            Vendors/Sponsors
          </Link>
          <Link 
            to="/faq"
            className="text-white text-2xl font-bold hover:text-pink-400 transition-colors flex items-center gap-2"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <HelpCircle className="w-6 h-6" />
            FAQs
          </Link>
          <Link 
            to="/welcome"
            className="text-white text-2xl font-bold hover:text-pink-400 transition-colors flex items-center gap-2"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid="header-mobile-welcome-link"
          >
            <Sparkles className="w-6 h-6" />
            Welcome
          </Link>
          <Link 
            to="/tequila"
            className="text-white text-2xl font-bold hover:text-pink-400 transition-colors flex items-center gap-2"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid="header-mobile-tequila-link"
          >
            <Wine className="w-6 h-6" />
            Tequila
          </Link>
          <Link 
            to="/map"
            className="text-white text-2xl font-bold hover:text-pink-400 transition-colors flex items-center gap-2"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid="header-mobile-map-link"
          >
            <MapIcon className="w-6 h-6" />
            Map
          </Link>
          <Link 
            to="/merch"
            className="text-white text-2xl font-bold hover:text-pink-400 transition-colors flex items-center gap-2"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid="header-mobile-merch-link"
          >
            <ShoppingBag className="w-6 h-6" />
            Merch
          </Link>
          <Link 
            to="/blog"
            className="text-white text-2xl font-bold hover:text-pink-400 transition-colors flex items-center gap-2"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid="header-mobile-blog-link"
          >
            <BookOpen className="w-6 h-6" />
            Blog
          </Link>
          <Button 
            onClick={openTickets}
            className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white text-lg font-bold rounded-full px-10 py-4 h-auto transition-all duration-300 flex items-center gap-2"
          >
            <Ticket className="w-5 h-5" />
            Get Tickets
          </Button>
        </nav>
      </div>
    </>
  );
};

export default Header;
